import { useState, useCallback, useMemo, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import { Platform, FlatList, Pressable } from 'react-native';
import { Text, View,  StyledModal } from '@constants/Themed';
import { H5, H6, H2 as Title } from 'tamagui';
import { useQuery } from '@tanstack/react-query';
import styles from '@stylesheet';
import apis from '@api/api';
import LottieView from '@utils/lottieAnimation';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import BottomSheet, { BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet';
import React from 'react';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { set } from 'lodash';
import Button from '@components/UI/Button/Button';
import { schemaCache } from '@utils/schemaCache';
import UserListItem from '../../UsersScreen/Components/UserListItem';
import { AntDesign } from '@expo/vector-icons';

interface AddTaskModalTypes {
  projectId: string;
  projectTitle: string;
  selectedWP: { work_package_title: string, _id: string };
  workPackageList: any;
  projectTeam: any[];
  onClose?: () => void;
  taskAssign: any;
  currentlyAssigned: any;
}

export interface AddTaskModalRef {
  openModal: (workPackageId?: string) => void;
  assignTask: (workPackageId: string, task: any) => void;
  closeModal: () => void;
}

const AddTaskModal = forwardRef<AddTaskModalRef, AddTaskModalTypes>((props, ref) => {
  const { projectId, projectTitle, workPackageList, projectTeam } = props;
  const modalRef = useRef(null);
  const [activeWP, setActiveWP] = useState(null);
  const [wpTitle, setWpTitle] = useState('');
  const [changeTask, setChangeTask] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [currentAssignees, setCurrentAssignees] = useState([]);

  useEffect(() => {
    return () => {
      setSelectedTeamMembers([]);
      setActiveWP(null);
      setWpTitle('');
      setChangeTask(false);
      setActiveTask(null);
      setCurrentAssignees([]);
    }
  }, []);

  useImperativeHandle(ref, () => ({
    openModal: (workPackageId?: string) => {
      if (workPackageId) {
        setActiveWP(workPackageId);
        const workPackage = workPackageList.find((wp: { _id: string; }) => wp._id === workPackageId);
        if (workPackage) {
          setWpTitle(workPackage.work_package_title);
        }
        console.log('Opening task modal for work package:', workPackageId, 'Title:', workPackage?.work_package_title);
      } else {
        setActiveWP(null);
        setWpTitle('');
        console.log('Opening task modal for work package selection');
      }
      if (Platform.OS === 'web') {
        modalRef.current!.openModal();
      } else {
        modalRef.current!.present();
      }
    },
    assignTask: (workPackageId, task) => {
      setActiveWP(workPackageId);
      setWpTitle(workPackageList.find((wp) => wp._id === workPackageId).work_package_title);
      setActiveTask(task);
      setCurrentAssignees(task.assigned_to);
      setChangeTask(true);
      if (Platform.OS === 'web') {
        modalRef.current!.openModal();
      } else {
        modalRef.current!.present();
      }
    },
    closeModal: () => {
      if (Platform.OS === 'web') {
        modalRef.current?.closeModal?.();
      } else {
        modalRef.current?.dismiss();
      }
    }
  }));


  const snapPoints = useMemo(() => ['30%', '80%'], []);

  const handleSheetChanges = useCallback((index: number) => {
    console.log('handleSheetChanges', index);
  }, []);

  type AddTaskModalTypes = {
    initialValues?: any;
  };
  const RenderAddTask = ({ initialValues }: AddTaskModalTypes) => {
    // Use cached schema
    const taskSchema = schemaCache.getSchema('taskSchema');



    return (
      <GenerateSchemaForm
        // sheetRef={ref}
          schema={taskSchema}
          initialData={initialValues}
        submitFunction={initialValues ? apis.modifyTask : apis.addTask}
          scrollEnabled={true}
        customStyling={{ maxHeight: 600 }}
        extraValues={{
          assigned_to: selectedTeamMembers,
          projectId: projectId,
          workPackageId: activeWP
        }}
        nullify={['createdAt', 'flagged', 'start_date', 'end_date', 'status', 'updated_at', 'completed', 'assigned_to', 'createdBy', 'access_list']}
          other={projectId}
        extraId={activeWP}
        extraCallBack={() => {
          if (Platform.OS === 'web') {
            modalRef.current!.closeModal?.();
            if (props.onClose) props.onClose();
          } else {
            modalRef.current?.dismiss();
            if (props.onClose) props.onClose();
          }
        }}
      />
    );
  };

  if (Platform.OS != 'web') {
    return (
      <BottomSheetModal
        ref={modalRef}
        snapPoints={snapPoints}
        onChange={handleSheetChanges}
        enablePanDownToClose={true}
        enableDismissOnClose={true}
        handleIndicatorStyle={{ backgroundColor: themeSelector() ? 'black' : 'white' }}
        backgroundStyle={{ backgroundColor: themeSelector() ? 'white' : 'black' }}
        style={styles.bottomSheet}
      >
        <BottomSheetView style={{ flex: 1 }}>
          <View style={{ flex: 1, paddingBottom: 80 }}>
            <View style={{ flexDirection: 'row', flex: 1, maxHeight: 50 }}>
              <H6 textAlign='center' fontSize={14} flexWrap='wrap'>Add a new task to {wpTitle}</H6>
            </View>
            <View style={{ minHeight: 500, flex: 1, minWidth: '100%', paddingRight: 20 }}>
              <RenderAddTask />
            </View>
          </View>
        </BottomSheetView>
      </BottomSheetModal>
    );
  } else {
    return (
      <StyledModal
        ref={modalRef}
        onModalHide={() => { setSelectedTeamMembers([]); setActiveWP(null); setWpTitle('') }}
        customContent={changeTask ? (
          <View style={{ flex: 1, backgroundColor: "transparent", gap: 30, padding: 20 }}>
            <Text style={{ color: "white" }}>Currently assigned to</Text>
            <FlatList
              data={currentAssignees}
              centerContent={!currentAssignees?.length}
              contentContainerStyle={{ paddingVertical: 20, gap: 20 }}
              ListEmptyComponent={<Title style={{ color: "white", textAlign: "center" }}>No team members found</Title>}
              renderItem={({ item, index }) => (
                <View
                  style={[styles.centerEverything, { minWidth: 250, flex: 1, flexDirection: "row", backgroundColor: "transparent", paddingHorizontal: 10, borderRadius: 20 }]}
                >
                  <UserListItem hideEmail removePermission textColor='white' item={item.profile_information} index={index} />
                  <AntDesign name="deleteuser" size={24} color="white" onPress={() => {
                    setCurrentAssignees(currentAssignees.filter((member) => member !== item));
                  }} />
                </View>
              )}>

            </FlatList>
            <FlatList
              data={projectTeam.filter((member) => { console.log(member); return !currentAssignees.includes(member) })}
              centerContent={!projectTeam?.length}
              key={currentAssignees.length}
              contentContainerStyle={{ paddingVertical: 20, gap: 20 }}
              ListEmptyComponent={<Title style={{ color: "white", textAlign: "center" }}>No team members found</Title>}
              renderItem={({ item, index }) => (
                <Pressable
                  style={{ backgroundColor: selectedTeamMembers.includes(item) ? 'rgb(90,192,234)' : "transparent", paddingHorizontal: 30, borderRadius: 20 }}
                  onPress={() => {
                    setCurrentAssignees([...currentAssignees, item]);
                  }}
                >
                  <UserListItem hideEmail removePermission textColor='white' item={item.profile_information} index={index} />
                </Pressable>
              )}
            />
          </View>
        ) : (
          <View style={{ flex: 1, backgroundColor: "transparent", gap: 30 }}>
            <Title style={{ color: "white" }}>Adding a new task to {wpTitle}</Title>
            <Text style={{ color: "white" }}>Selected users: {selectedTeamMembers?.length}</Text>
            <FlatList
              data={projectTeam}
              centerContent={!projectTeam?.length}
              contentContainerStyle={{ paddingVertical: 20, gap: 20 }}
              ListEmptyComponent={<Title style={{ color: "white", textAlign: "center" }}>No team members found</Title>}
              renderItem={({ item, index }) => (
                <Pressable
                  style={{ backgroundColor: selectedTeamMembers.includes(item) ? 'rgb(90,192,234)' : "transparent", paddingHorizontal: 30, borderRadius: 20 }}
                  onPress={() => {
                    if (selectedTeamMembers.includes(item)) {
                      setSelectedTeamMembers(selectedTeamMembers.filter((member) => member !== item))
                    } else {
                      setSelectedTeamMembers([...selectedTeamMembers, item])
                    }
                  }}
                >
                  <UserListItem hideEmail removePermission textColor='white' item={item.profile_information} index={index} />
                </Pressable>
              )}
              ListFooterComponent={
                <View style={{ flex: 1, backgroundColor: "transparent" }}>
                  <Button
                    buttonText={"Add new user to project"}
                    onPress={() => console.log('request to add user to project')}
                  />
                </View>
              }
            />
          </View>
        )}
      >
        {
          changeTask ?
            <View style={{ flex: 1, maxHeight: 600 }}>
              <Title >Users responsible for {activeTask.task_title}</Title>
              <RenderAddTask initialValues={activeTask} />
            </View>
            :
            <View style={{ flex: 1, backgroundColor: "transparent", maxHeight: 600 }}>
          {
                props.workPackageList?.length ? (
                  activeWP ? (
                    <View style={{ flex: 1, borderRadius: 20, backgroundColor: "transparent", paddingVertical: 30 }}>
                      <View style={{
                        marginBottom: 15,
                        padding: 10,
                        backgroundColor: 'rgba(0,255,0,0.1)',
                        borderRadius: 8
                      }}>
                        <Text style={{ color: 'white', fontSize: 14, fontWeight: 'bold' }}>
                          Selected Work Package: {wpTitle}
                        </Text>
                      </View>
                      <View style={{ flex: 1, backgroundColor: "transparent", overflow: "hidden" }}>
                        <RenderAddTask />
                      </View>
                    </View>
                  ) : (
                    <View style={{ flex: 1, backgroundColor: "transparent", padding: 20 }}>
                      <Title style={{ color: "white", marginBottom: 20 }}>
                        Select Work Package for Task
                      </Title>
                      <Text style={{ color: "white", marginBottom: 15 }}>
                        Choose which work package this task belongs to:
                      </Text>
                      <FlatList
                        data={props.workPackageList}
                        keyExtractor={(item) => item._id}
                        renderItem={({ item }) => (
                          <Pressable
                            style={{
                              backgroundColor: activeWP === item._id ? 'rgb(90,192,234)' : 'rgba(255,255,255,0.1)',
                              padding: 15,
                              borderRadius: 10,
                              marginVertical: 5,
                              borderWidth: 1,
                              borderColor: activeWP === item._id ? 'rgb(90,192,234)' : 'rgba(255,255,255,0.3)'
                            }}
                            onPress={() => {
                              setActiveWP(item._id);
                              setWpTitle(item.work_package_title);
                            }}
                          >
                            <Text style={{ color: 'white', fontSize: 16, fontWeight: 'bold' }}>
                              {item.work_package_title || 'Untitled Work Package'}
                            </Text>
                            {item.description && (
                              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 5 }}>
                                {item.description.length > 100 ? item.description.substring(0, 100) + '...' : item.description}
                              </Text>
                            )}
                            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, marginTop: 5 }}>
                              Tasks: {item.task_list?.length || 0}
                            </Text>
                          </Pressable>
                        )}
                      />
                    </View>
                  )
                ) : (
                  <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
                    <Text style={{ color: "white", fontSize: 16, textAlign: 'center', marginBottom: 10 }}>
                      No work packages available
                    </Text>
                    <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, textAlign: 'center' }}>
                      Please create a work package first before adding tasks
                    </Text>
              </View>
                  )
          }
        </View>
        }
      </StyledModal>
    );
  }
});

export default AddTaskModal;
